import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const AuthCallback = () => {
  const navigate = useNavigate();
  const { processGoogleCallback } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  const [errorDetails, setErrorDetails] = useState(null);

  const formatApiErrorDetail = (detail) => {
    if (detail == null) return null;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail
        .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
        .filter(Boolean)
        .join(' | ');
    }
    if (detail && typeof detail.msg === 'string') return detail.msg;
    try {
      return JSON.stringify(detail);
    } catch {
      return String(detail);
    }
  };

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      try {
        const hash = window.location.hash;
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        
        if (!sessionIdMatch) {
          setError('No session ID found in the URL');
          setErrorDetails('The Google OAuth redirect did not include a session_id fragment.');
          setTimeout(() => navigate('/login'), 5000);
          return;
        }

        const sessionId = sessionIdMatch[1];
        await processGoogleCallback(sessionId);
        
        // Clear the hash and navigate
        window.history.replaceState(null, '', window.location.pathname);
        navigate('/home');
      } catch (err) {
        console.error('Auth callback error:', err);
        
        // Extract specific upstream error message
        const status = err.response?.status;
        const detail = formatApiErrorDetail(err.response?.data?.detail);
        const message = detail || err.message || 'Google sign-in failed';
        
        setError(message);
        setErrorDetails(status ? `Status: ${status}` : null);
        setTimeout(() => navigate('/login'), 8000);
      }
    };

    processCallback();
  }, [navigate, processGoogleCallback]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] px-4">
        <div className="max-w-md w-full text-center bg-white rounded-2xl p-8 shadow-sm" data-testid="auth-callback-error">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">⚠️</span>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Outfit' }}>
            Google Sign-in Failed
          </h2>
          <div
            className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg p-3 mb-3 text-left break-words"
            data-testid="auth-callback-error-message"
          >
            {error}
          </div>
          {errorDetails && (
            <div
              className="text-gray-500 text-xs bg-gray-50 rounded-lg p-2 mb-4 text-left"
              data-testid="auth-callback-error-details"
            >
              {errorDetails}
            </div>
          )}
          <div className="text-gray-500 text-sm">Redirecting to login...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
      <div className="text-center" data-testid="auth-callback-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#009E60] border-t-transparent mx-auto mb-4"></div>
        <div className="text-gray-600">Completing sign in...</div>
      </div>
    </div>
  );
};

export default AuthCallback;
